/* Wishlist — localStorage backed, day-based expiry.
   Storage shape: { v: 1, items: [{ handle, addedAt }] }
   - Items older than EXPIRY_DAYS are pruned on read.
   - Updating an existing item refreshes its addedAt (rolling window).
*/
(function () {
  const KEY = 'm4u_wishlist';
  const EXPIRY_DAYS = 30;
  const EXPIRY_MS = EXPIRY_DAYS * 24 * 60 * 60 * 1000;

  function read() {
    try {
      const raw = localStorage.getItem(KEY);
      if (!raw) return [];
      const data = JSON.parse(raw);
      const items = Array.isArray(data.items) ? data.items : [];
      const now = Date.now();
      const fresh = items.filter(i => i && i.handle && (now - (i.addedAt || 0)) < EXPIRY_MS);
      if (fresh.length !== items.length) write(fresh);
      return fresh;
    } catch (e) {
      return [];
    }
  }

  function write(items) {
    try {
      localStorage.setItem(KEY, JSON.stringify({ v: 1, items }));
    } catch (e) {}
  }

  function has(handle) {
    return read().some(i => i.handle === handle);
  }

  function add(handle) {
    const items = read().filter(i => i.handle !== handle);
    items.unshift({ handle, addedAt: Date.now() });
    write(items);
  }

  function remove(handle) {
    write(read().filter(i => i.handle !== handle));
  }

  function toggle(handle) {
    if (has(handle)) { remove(handle); return false; }
    add(handle); return true;
  }

  function count() {
    return read().length;
  }

  function syncButton(btn) {
    const handle = btn.dataset.handle;
    if (!handle) return;
    const active = has(handle);
    btn.classList.toggle('is-active', active);
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    btn.setAttribute('aria-label', active ? 'Remove from wishlist' : 'Add to wishlist');
  }

  function syncAllButtons() {
    document.querySelectorAll('.js-wishlist-toggle').forEach(syncButton);
  }

  function syncCount() {
    const n = count();
    document.querySelectorAll('.js-wishlist-count').forEach(el => {
      el.textContent = n;
      el.toggleAttribute('hidden', n === 0);
    });
  }

  function onClick(e) {
    const btn = e.target.closest('.js-wishlist-toggle');
    if (!btn) return;
    e.preventDefault();
    toggle(btn.dataset.handle);
    syncAllButtons();
    syncCount();
    document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { count: count() } }));
  }

  /* ----- Wishlist page rendering ----- */
  async function renderWishlistPage(grid) {
    const items = read();
    const empty = document.querySelector('#wishlist-empty');
    if (!items.length) {
      grid.innerHTML = '';
      if (empty) empty.hidden = false;
      return;
    }
    if (empty) empty.hidden = true;

    grid.innerHTML = '<p class="wishlist__loading">Loading…</p>';

    const results = await Promise.all(items.map(async (item) => {
      try {
        const res = await fetch(`/products/${item.handle}?view=card`, { headers: { 'Accept': 'text/html' } });
        if (!res.ok) return null;
        const html = (await res.text()).trim();
        return html || null;
      } catch (e) {
        return null;
      }
    }));

    const html = results.filter(Boolean).join('');
    grid.innerHTML = html || '<p class="wishlist__empty-state">Your wishlist items are no longer available.</p>';
    syncAllButtons();
  }

  function init() {
    syncAllButtons();
    syncCount();
    document.addEventListener('click', onClick);

    const grid = document.querySelector('#wishlist-grid');
    if (grid) renderWishlistPage(grid);

    // Re-sync on storage change (other tabs)
    window.addEventListener('storage', e => {
      if (e.key === KEY) { syncAllButtons(); syncCount(); }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Public API
  window.M4UWishlist = { read, add, remove, has, toggle, count };
})();
